import * as React from "react";
import { routeOf, loginRoute } from "../../../utils/appRoutes";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { Bell as BellIcon, X as XIcon, Warning as WarningIcon, ClockCounterClockwise as HistoryIcon, Receipt as ReceiptIcon, Bank as BankIcon } from "@phosphor-icons/react";
import SellviaCleanupHistoryPanel from "./SellviaCleanupHistoryPanel";
import PageSearchBar from "./PageSearchBar";
import { List as ListIcon } from "@phosphor-icons/react";
import { Users as UsersIcon } from "@phosphor-icons/react";
import { SignOut, ChartBar, Sparkle } from "@phosphor-icons/react";
import { navIcons } from "./nav-icons";
import { MobileNav } from "./mobile-nav";
import { useSelector, useDispatch } from "react-redux";
import { isCustomerSide } from "../../../utils/accountSide";
import { useNavigate, useLocation } from "react-router-dom";
import { logout, setCredentials, setupTokenExpiration } from "../../../redux/slices/authSlice";
import ChatBot from "../../ChatBot/ChatBot";
import SearchBar from "../../shared/SearchBar";
import axios from "axios";
import NavStoreChips from "./NavStoreChips";
import { API_BASE_URL } from "../../../utils/config";
import { notifyError } from "../../../helper/notify";

// The old "Search modules…" box is hidden (its code below stays): the nav search now
// searches the data of the page that is open — see PageSearchBar / helper/pageSearch.js.
const SHOW_MODULE_SEARCH = false;


/* ── palette — MarketSync brand colors (cream/near-black/gold), the same
   two accent colors as the public landing page and the sidebar — replacing
   the old purple theme everywhere it was used as a token below. ── */
const N_LIGHT = {
  bg: "#f7f2ea",
  surface: "#ffffff",
  surfaceHigh: "#fdf3e3",
  border: "#e6dfd0",
  accent: "#b8892c",
  accentSoft: "rgba(184,137,44,0.10)",
  text: "#14161c",
  muted: "#6b6459",
  /* popover — cream card style, matching the rest of the app */
  popBg:     "#ffffff",
  popBgAlt:  "rgba(184,137,44,0.05)",
  popBorder: "#e6dfd0",
  popText:   "#14161c",
  popMuted:  "#6b6459",
  popAccent: "#b8892c",
  popPurple: "#b8892c",
  popGrad:   "linear-gradient(180deg,#8c6a1c,#6f5212)",
  success: "#10b981",
  error: "#ef4444",
};

const N_DARK = {
  bg: "#14161d",
  surface: "#0f1115",
  surfaceHigh: "#1a1d26",
  border: "rgba(255,255,255,0.08)",
  accent: "#d8b25a",
  accentSoft: "rgba(251,191,36,0.16)",
  text: "#e8e9ec",
  muted: "#a3a8b4",
  /* popover — glass style */
  popBg:     "rgba(23,26,33,0.92)",
  popBgAlt:  "rgba(251,191,36,0.08)",
  popBorder: "rgba(251,191,36,0.22)",
  popText:   "#e8e9ec",
  popMuted:  "#9a9fb0",
  popAccent: "#d8b25a",
  popPurple: "#d8b25a",
  popGrad:   "linear-gradient(180deg,#8c6a1c,#6f5212)",
  success: "#34d399",
  error: "#f87171",
};

/* ── shared glass popover card shell ──
   position is "fixed" at xs so left/right resolve against the viewport —
   these popovers live inside a small icon-button wrapper, so "absolute"
   would size left/right against that narrow wrapper instead of the screen. */
const POP_CARD_LIGHT = {
  position: { xs: "fixed", sm: "absolute" },
  top: { xs: 64, sm: "calc(100% + 10px)" },
  zIndex: 1400,
  background: "#fffdf9",
  backdropFilter: "blur(28px) saturate(1.8)",
  WebkitBackdropFilter: "blur(28px) saturate(1.8)",
  border: "1px solid #e6dfd0",
  borderRadius: "18px",
  boxShadow: "0 12px 48px rgba(184,137,44,0.14), 0 2px 16px rgba(31,36,48,0.08)",
  overflow: "hidden",
  minWidth: 280,
};

const POP_CARD_DARK = {
  ...POP_CARD_LIGHT,
  // Glass, but opaque enough to never wash out toward white regardless of
  // what's rendered behind it — matches the sidebar/dashboard glass tint.
  background: "linear-gradient(160deg, rgba(13,24,46,0.94), rgba(8,15,32,0.94))",
  border: "1px solid rgba(96,165,250,0.20)",
  boxShadow: "0 12px 48px rgba(0,0,0,0.45), 0 2px 16px rgba(0,0,0,0.30)",
};

export function MainNav() {
  const [openNav, setOpenNav] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchResults, setSearchResults] = React.useState([]);
  const [showSearch, setShowSearch] = React.useState(false);
  // Single source of truth for the 4 mutually-exclusive header panels —
  // opening one always closes the others (notif bell, new-customers, profile, AI chat).
  const [activePanel, setActivePanel] = React.useState(null); // null | "notif" | "contacts" | "profile" | "chat"
  const showNotif = activePanel === "notif";
  const showContacts = activePanel === "contacts";
  const showProfile = activePanel === "profile";
  const setShowNotif = (v) => setActivePanel(p => (typeof v === "function" ? v(p === "notif") : v) ? "notif" : (p === "notif" ? null : p));
  const setShowContacts = (v) => setActivePanel(p => (typeof v === "function" ? v(p === "contacts") : v) ? "contacts" : (p === "contacts" ? null : p));
  const setShowProfile = (v) => setActivePanel(p => (typeof v === "function" ? v(p === "profile") : v) ? "profile" : (p === "profile" ? null : p));

  const searchRef = React.useRef(null);
  const notifRef = React.useRef(null);
  const contactRef = React.useRef(null);
  const profileRef = React.useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, modules, role, token, twoFactorEnabled, side: accountSide } = useSelector((state) => state.auth);
  // Balance / Bill / Payout notifications are admin-side only — never fetched for customer-role users.
  // customer SIDE (any role created on the customer side), not only the role named "Customer"
  const isCustomerRole = isCustomerSide({ side: accountSide, role: role || user?.role });
  // /admin/* pages are admin-side only — a customer-role user would hit "Access denied",
  // so notification links that point there do nothing for customers.
  const adminNavigate = (path) => { if (!isCustomerRole) navigate(path); };
  // open an admin page by its page key (its URL comes from the database); nothing happens when this user has no such page
  const goAdmin = (pageKey, query = "") => { const p = routeOf(pageKey); if (p) adminNavigate(p + query); };
  const profileImage = user?.profilePicture;
  // The bar's own chrome now matches the rest of the app's cream/gold
  // MarketSync theme (previously always dark glass) — no theme toggle here
  // (removed from the profile menu below).
  const isDark = false;
  const N = isDark ? N_DARK : N_LIGHT;
  const popCard = isDark ? POP_CARD_DARK : POP_CARD_LIGHT;

  /* ── customer account notifications (SRS §5.6): unread count on the bell; the bell opens the Notifications page ── */
  const [accountUnread, setAccountUnread] = React.useState(0);
  React.useEffect(() => {
    if (!token || !isCustomerRole) return undefined;
    const load = () => axios.get(`${API_BASE_URL}/api/account/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setAccountUnread(data.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener('ms-notifications-changed', load);   // the Notifications page tells the bell when something was read
    return () => { clearInterval(t); window.removeEventListener('ms-notifications-changed', load); };
  }, [token, isCustomerRole]);

  /* ── stale order notifications ── */
  const [notifications, setNotifications] = React.useState([]);
  const [dismissedIds, setDismissedIds] = React.useState(new Set());
  const [seenIds,      setSeenIds]      = React.useState(new Set());
  const [toastCount,   setToastCount]   = React.useState(0);
  const [showToast,    setShowToast]    = React.useState(false);
  const toastTimerRef = React.useRef(null);

  /* ── Sellvia order-cleanup history panel ── */
  const [showCleanupHistory, setShowCleanupHistory] = React.useState(false);

  React.useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_BASE_URL}/api/sellvia/stale-order-notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setNotifications(data.notifications || []))
      .catch(() => {});
  }, [token]);

  const visibleNotifs = notifications.filter((n) => !dismissedIds.has(n.order_id));
  const unseenCount   = visibleNotifs.filter((n) => !seenIds.has(n.order_id)).length;

  const handleOpenNotif = () => {
    // customers: the bell opens the Notifications page of their account
    if (isCustomerRole) { navigate("/dashboard/notifications"); return; }
    setShowNotif((p) => {
      const opening = !p;
      if (opening) {
        // mark all currently visible as seen → badge → 0
        setSeenIds((prev) => new Set([...prev, ...visibleNotifs.map((n) => n.order_id)]));
        setSeenLowBalanceIds((prev) => new Set([...prev, ...visibleLowBalance.map((n) => n.store_id)]));
      }
      return opening;
    });
    setShowContacts(false);
    setShowProfile(false);
  };

  const handleDismissNotif = (orderId) => {
    setDismissedIds((prev) => new Set([...prev, orderId]));
    setTimeout(() => {
      setDismissedIds((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      // show toast for re-appeared notification
      setToastCount((c) => c + 1);
      setShowToast(true);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => {
        setShowToast(false);
        setToastCount(0);
      }, 4000);
    }, 5 * 60 * 1000);
  };

  const fmtDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' · '
      + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  /* ── order activity notifications (new/updated orders from 1-min cron) ── */
  const [activityNotifs,   setActivityNotifs]   = React.useState([]);
  const [activityToast,    setActivityToast]    = React.useState(null);
  const activityToastTimer = React.useRef(null);
  const prevActivityCount  = React.useRef(-1); // -1 = first load, don't toast yet

  const fetchActivityNotifs = React.useCallback(async () => {
    if (!token) return;
    try {
      const localMidnight = new Date();
      localMidnight.setHours(0, 0, 0, 0);
      const { data } = await axios.get(
        `${API_BASE_URL}/api/sellvia/order-activity-notifications?date_from=${localMidnight.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const notifs = data.notifications || [];
      setActivityNotifs(notifs);
      // Show toast only when count GROWS after the first successful load
      if (prevActivityCount.current >= 0 && notifs.length > prevActivityCount.current) {
        const newCount = notifs.length - prevActivityCount.current;
        setActivityToast({ count: newCount, latest: notifs[0] });
        clearTimeout(activityToastTimer.current);
        activityToastTimer.current = setTimeout(() => setActivityToast(null), 5000);
      }
      prevActivityCount.current = notifs.length;
    } catch {}
  }, [token]);

  React.useEffect(() => {
    fetchActivityNotifs();
    const id = setInterval(fetchActivityNotifs, 60000);
    return () => clearInterval(id);
  }, [fetchActivityNotifs]);

  const handleMarkActivityRead = React.useCallback(async (notifId) => {
    setActivityNotifs(prev => {
      const next = prev.filter(n => n._id !== notifId);
      prevActivityCount.current = next.length;
      return next;
    });
    try {
      await axios.post(
        `${API_BASE_URL}/api/sellvia/order-activity-notifications/${notifId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {}
  }, [token]);

  const handleMarkAllActivityRead = React.useCallback(async () => {
    // Clear activity notifs from state
    setActivityNotifs([]);
    prevActivityCount.current = 0;
    // Also clear stale order notifs from state (client-side dismiss — no API needed)
    setNotifications([]);
    // Also clear low-balance notifs (also client-side only — live-computed, no DB record)
    setLowBalanceNotifs([]);
    prevLowBalanceCount.current = 0;
    // Also clear billing notifs
    setBillingNotifs([]);
    prevBillingCount.current = 0;
    // Also clear payout Bank-Status notifs (also client-side only)
    setPayoutBankNotifs([]);
    try {
      await axios.post(
        `${API_BASE_URL}/api/sellvia/order-activity-notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await axios.post(
        `${API_BASE_URL}/api/sellvia/billing-notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {}
  }, [token]);

  /* ── billing notifications ("new bill" from the billing sync cron) ──
     Same DB-backed, poll-every-60s pattern as order-activity notifs above —
     clicking one navigates to the Billing page and opens that store's
     current bill (see BillPage.jsx's focusRequest/?store= handling). */
  const [billingNotifs,     setBillingNotifs]     = React.useState([]);
  const [billingToast,      setBillingToast]      = React.useState(null);
  const billingToastTimer   = React.useRef(null);
  const prevBillingCount    = React.useRef(-1); // -1 = first load, don't toast yet

  const fetchBillingNotifs = React.useCallback(async () => {
    if (!token || isCustomerRole) return;
    try {
      const localMidnight = new Date();
      localMidnight.setHours(0, 0, 0, 0);
      const { data } = await axios.get(
        `${API_BASE_URL}/api/sellvia/billing-notifications?date_from=${localMidnight.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const notifs = data.notifications || [];
      setBillingNotifs(notifs);
      if (prevBillingCount.current >= 0 && notifs.length > prevBillingCount.current) {
        const newCount = notifs.length - prevBillingCount.current;
        setBillingToast({ count: newCount, latest: notifs[0] });
        clearTimeout(billingToastTimer.current);
        billingToastTimer.current = setTimeout(() => setBillingToast(null), 5000);
      }
      prevBillingCount.current = notifs.length;
    } catch {}
  }, [token, isCustomerRole]);

  React.useEffect(() => {
    fetchBillingNotifs();
    const id = setInterval(fetchBillingNotifs, 60000);
    return () => clearInterval(id);
  }, [fetchBillingNotifs]);

  const handleMarkBillingRead = React.useCallback(async (notifId) => {
    setBillingNotifs(prev => {
      const next = prev.filter(n => n._id !== notifId);
      prevBillingCount.current = next.length;
      return next;
    });
    try {
      await axios.post(
        `${API_BASE_URL}/api/sellvia/billing-notifications/${notifId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {}
  }, [token]);

  // Clicking a billing notification: mark it read, then go to the Billing
  // page with ?store=<id> so it auto-opens/scrolls to that store's bill.
  const handleBillingNotifClick = React.useCallback((n) => {
    handleMarkBillingRead(n._id);
    setShowNotif(false);
    goAdmin("admin.bill", `?store=${encodeURIComponent(n.store_id)}`);
  }, [handleMarkBillingRead, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── new customer notifications ──
     Same "don't toast on first load" pattern as order-activity notifs above:
     prevCustomerCount starts at -1 (sentinel meaning "haven't fetched yet"), so
     whatever is already in the DB when you log in just populates the bell
     badge silently. Only a count INCREASE on a later poll (a real new order
     saved to the DB while you're online) triggers the bottom-right toast —
     polled every 60s so it fires close to the moment the order was saved,
     not just once at login. */
  const [customerNotifs,      setCustomerNotifs]      = React.useState([]);
  const [dismissedCustomers,  setDismissedCustomers]  = React.useState(new Set());
  const [seenCustomerIds,     setSeenCustomerIds]     = React.useState(new Set());
  const [showCustomerToast,   setShowCustomerToast]   = React.useState(false);
  const [customerToastData,   setCustomerToastData]   = React.useState(null);
  const customerToastTimer    = React.useRef(null);
  const prevCustomerCount     = React.useRef(-1); // -1 = first load, don't toast yet

  const fetchCustomerNotifs = React.useCallback(async () => {
    if (!token) return;
    try {
      // Pass local midnight so timezone matches the orders page "today" filter
      const localMidnight = new Date();
      localMidnight.setHours(0, 0, 0, 0);
      const { data } = await axios.get(
        `${API_BASE_URL}/api/sellvia/new-customer-notifications?date_from=${localMidnight.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const notifs = data.notifications || [];
      setCustomerNotifs(notifs);
      // Show toast only when count GROWS after the first successful load
      if (prevCustomerCount.current >= 0 && notifs.length > prevCustomerCount.current) {
        setCustomerToastData(notifs[0]);
        setShowCustomerToast(true);
        if (customerToastTimer.current) clearTimeout(customerToastTimer.current);
        customerToastTimer.current = setTimeout(() => setShowCustomerToast(false), 5000);
      }
      prevCustomerCount.current = notifs.length;
    } catch (err) {
      console.error('[CustomerNotif] fetch error:', err?.response?.data || err?.message);
    }
  }, [token]);

  React.useEffect(() => {
    fetchCustomerNotifs();
    const id = setInterval(fetchCustomerNotifs, 60000);
    return () => clearInterval(id);
  }, [fetchCustomerNotifs]);

  const visibleCustomers  = customerNotifs.filter((n) => !dismissedCustomers.has(n.order_id));
  const unseenCustomers   = visibleCustomers.filter((n) => !seenCustomerIds.has(n.order_id)).length;

  const handleOpenContacts = () => {
    setShowContacts((p) => {
      const opening = !p;
      if (opening) {
        setSeenCustomerIds((prev) => new Set([...prev, ...visibleCustomers.map((n) => n.order_id)]));
      }
      return opening;
    });
    setShowNotif(false);
    setShowProfile(false);
  };

  const handleDismissCustomer = (orderId) => {
    setDismissedCustomers((prev) => new Set([...prev, orderId]));
  };

  /* ── low Sellvia Ads balance notifications ──
     Live-computed on the backend (not DB-backed), so it's just polled —
     same "count grows → toast" pattern as the customer notifications above,
     but checked every 2 hours since a store's ad balance doesn't change
     nearly as fast as new orders do. */
  const [lowBalanceNotifs,      setLowBalanceNotifs]      = React.useState([]);
  const [dismissedLowBalance,   setDismissedLowBalance]   = React.useState(new Set());
  const [seenLowBalanceIds,     setSeenLowBalanceIds]     = React.useState(new Set());
  const [showLowBalanceToast,   setShowLowBalanceToast]   = React.useState(false);
  const [lowBalanceToastData]   = React.useState(null); // the Ads pop-up toast is retired (see fetchLowBalanceNotifs)
  const prevLowBalanceCount     = React.useRef(-1); // -1 = first load, don't toast yet

  const fetchLowBalanceNotifs = React.useCallback(async () => {
    if (!token || isCustomerRole) return;
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/sellvia/low-balance-notifications`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const notifs = data.notifications || [];
      setLowBalanceNotifs(notifs);
      // No pop-up toast for Ads balance any more — the bottom-right alert is Balances-page news only (payout ready).
      // The Ads items still list inside the bell dropdown.
      prevLowBalanceCount.current = notifs.length;
    } catch (err) {
      console.error('[LowBalanceNotif] fetch error:', err?.response?.data || err?.message);
    }
  }, [token, isCustomerRole]);

  React.useEffect(() => {
    fetchLowBalanceNotifs();
    const id = setInterval(fetchLowBalanceNotifs, 2 * 60 * 60 * 1000); // every 2 hours
    return () => clearInterval(id);
  }, [fetchLowBalanceNotifs]);

  const visibleLowBalance = lowBalanceNotifs.filter((n) => !dismissedLowBalance.has(n.store_id));
  const unseenLowBalance  = visibleLowBalance.filter((n) => !seenLowBalanceIds.has(n.store_id)).length;

  const handleDismissLowBalance = (storeId) => {
    setDismissedLowBalance((prev) => new Set([...prev, storeId]));
  };

  const location = useLocation();

  /* ── payout Bank-Status reminder ── Sent 3+ days ago, still Pending.
     Same live-computed, no-DB-record pattern as low-balance above — polled
     on the same 2-hour cadence since neither changes fast enough to need
     more. */
  const [payoutBankNotifs, setPayoutBankNotifs] = React.useState([]);

  const fetchPayoutBankNotifs = React.useCallback(async () => {
    if (!token || isCustomerRole) return;
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/sellvia/payout-bankstatus-notifications`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayoutBankNotifs(data.notifications || []);
    } catch (err) {
      console.error('[PayoutBankNotif] fetch error:', err?.response?.data || err?.message);
    }
  }, [token, isCustomerRole]);

  React.useEffect(() => {
    fetchPayoutBankNotifs();
    const id = setInterval(fetchPayoutBankNotifs, 2 * 60 * 60 * 1000); // every 2 hours
    return () => clearInterval(id);
  }, [fetchPayoutBankNotifs]);

  // Dismiss is client-side only, same as low-balance above — this is
  // live-computed (no DB record), so there's nothing to mark read server-side.
  const [dismissedPayoutBankIds, setDismissedPayoutBankIds] = React.useState(new Set());
  const visiblePayoutBank = payoutBankNotifs.filter((n) => !dismissedPayoutBankIds.has(n.store_id));
  const handleDismissPayoutBank = (storeId) => {
    setDismissedPayoutBankIds((prev) => new Set([...prev, storeId]));
  };

  /* ── Task Manager notifications (staff only; the bell shows the unread ones) ── */
  const [taskNotifs, setTaskNotifs] = React.useState([]);
  React.useEffect(() => {
    if (!token || isCustomerRole) return undefined;
    const pull = () => axios
      .get(`${API_BASE_URL}/api/tasks/notifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setTaskNotifs((data.data || []).filter((n) => !n.is_read)))
      .catch(() => {}); // no access to the Task Manager, or it is offline: the bell simply shows no tasks
    pull();
    const timer = setInterval(pull, 60000);
    return () => clearInterval(timer);
  }, [token, isCustomerRole]);
  const taskUnreadCount = taskNotifs.length;
  const handleTaskNotifClick = (n) => {
    setTaskNotifs((prev) => prev.filter((x) => x._id !== n._id));
    axios.post(`${API_BASE_URL}/api/tasks/notifications/read`, { ids: [n._id] }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    setShowNotif(false);
    adminNavigate(`/admin/my-day?task=${n.task_id}`);
  };
  const handleMarkTasksRead = () => {
    setTaskNotifs([]);
    axios.post(`${API_BASE_URL}/api/tasks/notifications/read`, { all: true }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  /* ── payout-ready reminder (Balances page) — live-computed, polled every 2 hours ── */
  const [payoutReadyNotifs, setPayoutReadyNotifs] = React.useState([]);
  const fetchPayoutReadyNotifs = React.useCallback(async () => {
    if (!token || isCustomerRole) return;
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/sellvia/payout-ready-notifications`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayoutReadyNotifs(data.notifications || []);
    } catch (err) {
      console.error('[PayoutReadyNotif] fetch error:', err?.response?.data || err?.message);
    }
  }, [token, isCustomerRole]);
  React.useEffect(() => {
    fetchPayoutReadyNotifs();
    const id = setInterval(fetchPayoutReadyNotifs, 2 * 60 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchPayoutReadyNotifs]);

  /* ── recurring navbar alert ticker — one store at a time ──
     Combines low-Ads-balance and overdue-payout-Bank-Status into one queue,
     each grouped company-by-company (every one of company A's flagged
     stores, then every one of company B's, ...), and cycles through them
     one message at a time in the header strip. Starts 10s after this bar
     mounts (i.e. shortly after login), shows each message for 6s, then
     after the LAST item in the full list waits 5 minutes before starting
     the whole pass again — for as long as the session stays logged in.
     A message whose target page is the page you're already on is skipped
     (no point nagging you about the store you're already looking at). */
  const groupByCompany = (items) => {
    const byCompany = new Map();
    items.forEach((n) => {
      const key = n.sellvia_dashboard_id || 'unknown';
      if (!byCompany.has(key)) byCompany.set(key, []);
      byCompany.get(key).push(n);
    });
    return [...byCompany.values()].flat();
  };

  // Bottom-right ticker = Balances-page news only: a store with $100+ available and no payout taken for 3+ days.
  const balancesPath = routeOf("admin.balances");
  const alertQueue = React.useMemo(() => (
    groupByCompany(payoutReadyNotifs).map((n) => ({
      key: `payout-ready-${n.store_id}`,
      message: `${n.store_name} (${n.company_name}) — $${Number(n.available).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} available, payout ready — please take it`
        + (n.days_since_payout == null ? '' : ` · last payout ${n.days_since_payout}d ago`),
      path: `${balancesPath}?store=${encodeURIComponent(n.store_id)}`,
      hideOnPath: balancesPath,
    })).filter((item) => !location.pathname.startsWith(item.hideOnPath))
  ), [payoutReadyNotifs, location.pathname, balancesPath]);

  // Read via a ref inside the recursive ticker below so a data refetch
  // (every 2h) updates what's shown WITHOUT resetting the 10s-after-login /
  // 5-minutes-after-a-full-pass timing that effect owns — that effect runs
  // once per mount (login), on purpose.
  const alertQueueRef = React.useRef([]);
  React.useEffect(() => { alertQueueRef.current = alertQueue; }, [alertQueue]);

  const [alertIndex,       setAlertIndex]       = React.useState(0);
  const [showAlertTicker,  setShowAlertTicker]  = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    let idx = 0;
    const ITEM_DURATION = 6000;         // how long each message shows
    const GAP_BETWEEN_ITEMS = 400;      // brief blank between messages
    const CYCLE_PAUSE = 5 * 60 * 1000;  // pause after a full pass through the list
    const RETRY_WHEN_EMPTY = 30 * 1000; // nothing to show yet — check again soon
    let timer;

    const tick = () => {
      if (cancelled) return;
      const queue = alertQueueRef.current;
      if (queue.length === 0) {
        setShowAlertTicker(false);
        timer = setTimeout(tick, RETRY_WHEN_EMPTY);
        return;
      }
      idx = idx % queue.length;
      setAlertIndex(idx);
      setShowAlertTicker(true);
      const isLast = idx === queue.length - 1;
      timer = setTimeout(() => {
        if (cancelled) return;
        setShowAlertTicker(false);
        idx += 1;
        timer = setTimeout(tick, isLast ? CYCLE_PAUSE : GAP_BETWEEN_ITEMS);
      }, ITEM_DURATION);
    };

    timer = setTimeout(tick, 10 * 1000); // 10s after login
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // once per mount — see alertQueueRef above for why

  const currentAlert = showAlertTicker ? (alertQueue[alertIndex] || null) : null;

  const handleAlertClick = () => {
    if (!currentAlert) return;
    setShowAlertTicker(false);
    navigate(currentAlert.path);
  };

  /* helper: consistent color from customer name */
  const nameColor = (name = '') => {
    const palette = ['#b8892c','#2563eb','#059669','#d97706','#dc2626','#0891b2','#be185d','#0f766e'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
  };

  // /* helper: time ago */
  // const timeAgo = (d) => {
  //   if (!d) return '';
  //   const diff = Date.now() - new Date(d);
  //   const mins = Math.floor(diff / 60000);
  //   if (mins < 1)  return 'just now';
  //   if (mins < 60) return `${mins}m ago`;
  //   const hrs = Math.floor(mins / 60);
  //   if (hrs < 24)  return `${hrs}h ago`;
  //   return `${Math.floor(hrs / 24)}d ago`;
  // };

  /* ── search: filter modules as user types ── */
  React.useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) { setSearchResults([]); return; }
    const results = (modules || []).filter(m =>
      m.name && m.route_path && m.name.toLowerCase().includes(q)
    );
    setSearchResults(results);
  }, [searchQuery, modules]);

  const handleSearchSelect = (routePath) => {
    navigate(routePath);
    setSearchQuery("");
    setSearchResults([]);
    setShowSearch(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && searchResults.length > 0) {
      handleSearchSelect(searchResults[0].route_path);
    }
    if (e.key === "Escape") { setShowSearch(false); setSearchQuery(""); }
  };

  /* ── instant logout ── */
  // const handleLogout = () => {
  //   dispatch(logout());
  //   navigate("/login", { replace: true });
  // };

  const handleLogout = () => {
    // Save current page before logout
    const currentPath = window.location.pathname + window.location.search;
    const isAdminPath = currentPath.startsWith('/admin');
    const saveKey = isAdminPath ? 'lastAdminPage' : 'lastUserPage';
    sessionStorage.setItem(saveKey, currentPath);
    console.log('[LastPage] main-nav logout: saved', saveKey, '=', currentPath);

    dispatch(logout());

    // Redirect based on current page — the admin login route is "/admin"
    // itself (the "admin-login" route), there is no separate "/admin/login" route.
    const redirectTo = loginRoute(isAdminPath);
    console.log('[LastPage] main-nav logout: navigating to', redirectTo);
    navigate(redirectTo, { replace: true });
  };

  /* ── session expiry warning ── */
  const [sessionSecsLeft,    setSessionSecsLeft]    = React.useState(null);
  const [showSessionWarning, setShowSessionWarning] = React.useState(false);
  const [renewLoading,       setRenewLoading]       = React.useState(false);

  // React.useEffect(() => {
  //   if (!token) return;
  //   let expMs;
  //   try {
  //     const payload = JSON.parse(atob(token.split('.')[1]));
  //     expMs = (payload.exp || 0) * 1000;
  //   } catch { return; }
  //   if (!expMs) return;

  //   const tick = () => {
  //     const remaining = Math.round((expMs - Date.now()) / 1000);
  //     setSessionSecsLeft(remaining);
  //     if (remaining <= 30 && remaining > 0) {
  //       setShowSessionWarning(true);
  //     } else if (remaining <= 0) {
  //       dispatch(logout());
  //       navigate('/login?expired=true', { replace: true });
  //     }
  //   };

  //   tick();
  //   const id = setInterval(tick, 1000);
  //   return () => clearInterval(id);
  // }, [token, dispatch, navigate]);

    React.useEffect(() => {
    if (!token) return;
    let expMs;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      expMs = (payload.exp || 0) * 1000;
    } catch { return; }
    if (!expMs) return;

    const tick = () => {
      const remaining = Math.round((expMs - Date.now()) / 1000);
      setSessionSecsLeft(remaining);
      if (remaining <= 30 && remaining > 0) {
        setShowSessionWarning(true);
      } else if (remaining <= 0) {
        // Save current page before session expiry logout
        const currentPath = window.location.pathname + window.location.search;
        const isAdminPath = currentPath.startsWith('/admin');
        const saveKey = isAdminPath ? 'lastAdminPage' : 'lastUserPage';
        sessionStorage.setItem(saveKey, currentPath);
        console.log('[LastPage] session-expiry logout: saved', saveKey, '=', currentPath);

        dispatch(logout());
        const redirectTo = `${loginRoute(isAdminPath)}?expired=true`;
        console.log('[LastPage] session-expiry logout: navigating to', redirectTo);
        navigate(redirectTo, { replace: true });
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [token, dispatch, navigate]);


  const handleRenewSession = async () => {
    if (renewLoading) return;
    setRenewLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/auth/refresh-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.token) {
        const newPayload = JSON.parse(atob(res.data.token.split('.')[1]));
        const secsLeft = (newPayload.exp || 0) - Math.floor(Date.now() / 1000);
        dispatch(setCredentials({ token: res.data.token }));
        dispatch(setupTokenExpiration(secsLeft));
        setShowSessionWarning(false);
      }
    } catch (err) {
      console.error('[session-renewal] failed:', err.response?.data || err.message);
    } finally {
      setRenewLoading(false);
    }
  };

  // ── 2FA-gated session renewal — only for accounts with Two-Step
  // Verification on (per-user, confirmed at login and kept in Redux). The
  // countdown banner and its default silent handleRenewSession above stay
  // completely unchanged for everyone else; this just intercepts the
  // "Renew" click for 2FA accounts and asks for a verification method
  // first, calling /session/verify instead of the plain /refresh-token.
  const [sessionVerifyOpen, setSessionVerifyOpen] = React.useState(false);
  const [sessionVerifyMethod, setSessionVerifyMethod] = React.useState(null); // null | 'email' | 'authenticator'
  const [sessionOtp, setSessionOtp] = React.useState("");
  const [sessionSendingEmail, setSessionSendingEmail] = React.useState(false);
  const [sessionVerifying, setSessionVerifying] = React.useState(false);

  const handleRenewClick = () => {
    if (twoFactorEnabled) {
      setSessionVerifyMethod(null);
      setSessionOtp("");
      setSessionVerifyOpen(true);
      return;
    }
    handleRenewSession();
  };

  const closeSessionVerify = () => {
    setSessionVerifyOpen(false);
    setSessionVerifyMethod(null);
    setSessionOtp("");
  };

  const chooseSessionEmail = async () => {
    setSessionSendingEmail(true);
    try {
      await axios.post(`${API_BASE_URL}/api/auth/session/send-email-otp`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSessionVerifyMethod('email');
    } catch (err) {
      console.error('[session-renewal] send email otp failed:', err.response?.data || err.message);
      notifyError(err.response?.data?.message || 'Failed to send the verification code.');
    } finally {
      setSessionSendingEmail(false);
    }
  };

  const confirmSessionOtp = async () => {
    if (sessionOtp.length < 6) return;
    setSessionVerifying(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/auth/session/verify`,
        { method: sessionVerifyMethod, otp: sessionOtp },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.token) {
        const newPayload = JSON.parse(atob(res.data.token.split('.')[1]));
        const secsLeft = (newPayload.exp || 0) - Math.floor(Date.now() / 1000);
        dispatch(setCredentials({ token: res.data.token }));
        dispatch(setupTokenExpiration(secsLeft));
        setShowSessionWarning(false);
        closeSessionVerify();
      }
    } catch (err) {
      console.error('[session-renewal] verify failed:', err.response?.data || err.message);
      // wrong code: "Incorrect code. Only N attempts remaining." — or the 1-hour lock message
      notifyError(err.response?.data?.message || 'Verification failed. Please try again.');
      setSessionOtp('');
    } finally {
      setSessionVerifying(false);
    }
  };

  /* ── accessible modules for profile dropdown ── */
  // Account & Profile (admin or customer side), and under it the pages that are opened from here instead of from the sidebar
  // (DbeatTech Inquiries) — no Settings, no other module. Exact paths (read from the database by page key), no name-based matching.
  // Redux modules are already permission-filtered, so each one shows only if the user has it.
  const ACCOUNT_PATHS = new Set([isCustomerRole ? routeOf("customer.account") : routeOf("admin.account")].filter(Boolean)); // only the user's own side — an admin role may also hold the customer Account module
  const PROFILE_PATHS = new Set([...ACCOUNT_PATHS, routeOf("admin:DbeatTechInquiriesPage")].filter(Boolean));
  const accessibleModules = (modules || []).filter(m =>
    m.route_path && PROFILE_PATHS.has(m.route_path.trim())
  ).sort((a, b) => Number(ACCOUNT_PATHS.has(b.route_path.trim())) - Number(ACCOUNT_PATHS.has(a.route_path.trim()))); // Account & Profile first

  // The order / bill / new-customer / low-balance toasts stack up from the bottom-right corner (84px each).
  // The round Ask-AI button lives in that corner too, so it lifts above them (--ChatFab-bottom, used by ChatBot.jsx).
  const visibleToastCount = [showToast, !!activityToast, !!billingToast, !!(showCustomerToast && customerToastData), !!(showLowBalanceToast && lowBalanceToastData)].filter(Boolean).length;
  React.useEffect(() => {
    document.body.style.setProperty("--ChatFab-bottom", `${24 + 84 * (visibleToastCount + (currentAlert ? 1 : 0))}px`);
    return () => { document.body.style.removeProperty("--ChatFab-bottom"); };
  }, [visibleToastCount, currentAlert]);

  /* ── icon button style ── */
  const iconBtnSx = {
    width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, borderRadius: "10px",
    color: N.muted,
    border: "1px solid transparent",
    transition: "all 0.18s ease",
    "&:hover": {
      color: N.accent,
      background: N.accentSoft,
      border: `1px solid rgba(184,137,44,0.2)`,
    },
  };

  // Icon-over-label stack — Customers / History of Order Process /
  // Notifications: icon on top, a small label underneath it, instead of
  // a side-by-side icon+text button.
  const navBtnStackSx = {
    height: { xs: 40, sm: 46 }, minWidth: { xs: 40, sm: "auto" },
    px: { xs: 0.5, sm: 1.2 }, py: 0.5, borderRadius: "10px",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px",
    color: N.muted,
    border: "1px solid transparent",
    transition: "all 0.18s ease",
    "&:hover": {
      color: N.accent,
      background: N.accentSoft,
      border: `1px solid rgba(184,137,44,0.2)`,
    },
  };
  const navBtnStackLabelSx = { display: { xs: "none", sm: "block" }, fontSize: "0.62rem", fontWeight: 700, textTransform: "none", whiteSpace: "nowrap", color: "inherit" };

  return (
    <>
      <Box
        component="header"
        sx={{
          borderBottom: "1px solid #e6dfd0",
          position: "sticky",
          top: 0,
          zIndex: 1200,
          background: "#f7f2ea",
          boxShadow: "0 2px 16px rgba(31,36,48,0.06)",
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: "60px",
            px: { xs: 1, sm: 3 }, gap: 0.5,
          }}
        >
          {/* LEFT: hamburger + search + AI chat — brand logo/"MarketSync"
              text now lives in the sidebar (SideNav) only. */}
          <Stack direction="row" spacing={{ xs: 0.75, sm: 1.5 }} sx={{ alignItems: "center", minWidth: 0, flex: "0 0 auto" }}>
            <IconButton
              onClick={() => {
                setOpenNav(true);
                // Closing the AI chat drawer too — both are full-height
                // overlays on mobile and shouldn't be open at once.
                setActivePanel(p => (p === "chat" ? null : p));
              }}
              sx={{ display: { lg: "none" }, ...iconBtnSx }}
            >
              <ListIcon size={20} />
            </IconButton>

            {/* Search modules — hidden (SHOW_MODULE_SEARCH), code kept */}
            {SHOW_MODULE_SEARCH && (
            <ClickAwayListener onClickAway={() => { setShowSearch(false); setSearchQuery(""); setSearchResults([]); }}>
              <Box ref={searchRef} sx={{ position: "relative", flex: { xs: 1, sm: "none" }, minWidth: 0 }}>
                <SearchBar
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setShowSearch(true); }}
                  placeholder="Search modules..."
                  sx={{ width: { xs: "100%", sm: 260 } }}
                  inputProps={{ onFocus: () => setShowSearch(true), onKeyDown: handleKeyPress }}
                />

                {/* Search dropdown */}
                {showSearch && searchQuery.trim() && (
                  <Fade in>
                    <Box sx={{ ...popCard, left: { xs: 8, sm: 0 }, width: { xs: "calc(100vw - 16px)", sm: "auto" }, minWidth: { xs: 0, sm: 280 } }}>
                      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${N.popBorder}` }}>
                        <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: N.popMuted }}>
                          {searchResults.length > 0 ? `${searchResults.length} module${searchResults.length > 1 ? "s" : ""} found` : "No modules found"}
                        </Typography>
                      </Box>
                      {searchResults.length > 0 ? (
                        <Box sx={{ py: 1, maxHeight: 260, overflowY: "auto" }}>
                          {searchResults.map((m) => (
                            <Box
                              key={m.moduleId || m._id}
                              onClick={() => handleSearchSelect(m.route_path)}
                              sx={{
                                px: 2, py: 1.2,
                                display: "flex", alignItems: "center", gap: 1.5,
                                cursor: "pointer",
                                transition: "background 0.14s",
                                "&:hover": { background: "rgba(184,137,44,0.1)" },
                              }}
                            >
                              <Box sx={{ width: 30, height: 30, borderRadius: "8px", background: N.accentSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <ChartBar size={15} color={N.accent} />
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: N.popText }}>{m.name}</Typography>
                                <Typography sx={{ fontSize: "0.7rem", color: N.popMuted }}>{m.route_path}</Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
                          <Typography sx={{ fontSize: "0.82rem", color: N.popMuted }}>No matching module for "{searchQuery}"</Typography>
                        </Box>
                      )}
                    </Box>
                  </Fade>
                )}
              </Box>
            </ClickAwayListener>
            )}

            {/* AI Chatbot button — the chat panel now opens as a full-height
                drawer pinned to the right edge, so the mobile sidebar drawer
                (openNav) is closed automatically to avoid the two overlapping. */}
            <ChatBot
              hideTriggerOnMobile
              open={activePanel === "chat"}
              onOpenChange={(v) => {
                setActivePanel(p => (v ? "chat" : (p === "chat" ? null : p)));
                if (v) setOpenNav(false);
              }}
            />
          </Stack>

          {/* CENTER: search — searches the data of the page that is open (helper/pageSearch.js) */}
          <Box sx={{ flex: "1 1 0", minWidth: 0, display: "flex", justifyContent: "center", px: { xs: 0.5, sm: 2 } }}>
            <PageSearchBar />
          </Box>

          {/* RIGHT: icons + avatar */}
          <Stack direction="row" spacing={{ xs: 0.25, sm: 1 }} sx={{ alignItems: "center", flexShrink: 0 }}>

            {/* New Customers */}
            <ClickAwayListener onClickAway={() => setShowContacts(false)}>
              <Box ref={contactRef} sx={{ position: "relative" }}>
                <Tooltip title="New Customers">
                  <Box sx={{ position: "relative", display: { xs: "none", sm: "inline-flex" } }}>
                    <Button onClick={handleOpenContacts} sx={navBtnStackSx}>
                      <UsersIcon size={18} />
                      <Box component="span" sx={navBtnStackLabelSx}>Customers</Box>
                    </Button>
                    {unseenCustomers > 0 && (
                      <Box sx={{
                        position: "absolute", top: 3, right: 3,
                        minWidth: 16, height: 16, borderRadius: "8px",
                        background: "#b8892c",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: "2px solid #fff",
                        pointerEvents: "none",
                      }}>
                        <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, color: "#fff", lineHeight: 1, px: "2px" }}>
                          {unseenCustomers > 9 ? "9+" : unseenCustomers}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Tooltip>

                {showContacts && (
                  <Box sx={{ ...popCard, right: { xs: 8, sm: 0 }, left: { xs: 8, sm: "auto" }, minWidth: { xs: 0, sm: 360 }, maxWidth: { xs: "calc(100vw - 16px)", sm: 400 } }}>
                    {/* Gradient top bar */}
                    <Box sx={{ background: N.popGrad, height: 3 }} />
                    {/* Header */}
                    <Box sx={{ px: 2, py: 1.3, borderBottom: "1px solid rgba(184,137,44,0.10)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: "rgba(184,137,44,0.10)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <UsersIcon size={14} color={N.accent} weight="fill" />
                        </Box>
                        <Typography sx={{ fontSize: "0.86rem", fontWeight: 800, color: N.popText }}>Today's Customers</Typography>
                        {visibleCustomers.length > 0 && (
                          <Box sx={{ px: 0.8, py: 0.15, borderRadius: "20px", background: "rgba(184,137,44,0.10)", border: "1px solid rgba(184,137,44,0.25)" }}>
                            <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, color: N.accent }}>{visibleCustomers.length}</Typography>
                          </Box>
                        )}
                      </Box>
                      <IconButton size="small" onClick={() => setShowContacts(false)} sx={{ p: 0.45, borderRadius: "7px", color: N.popMuted, "&:hover": { color: N.accent, background: "rgba(184,137,44,0.08)" } }}>
                        <XIcon size={14} weight="bold" />
                      </IconButton>
                    </Box>

                    {visibleCustomers.length === 0 ? (
                      <Box sx={{ px: 2.5, py: 4, textAlign: "center" }}>
                        <Box sx={{ width: 48, height: 48, borderRadius: "14px", background: "rgba(184,137,44,0.08)", border: "1px solid rgba(184,137,44,0.18)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                          <UsersIcon size={22} color={N.accent} />
                        </Box>
                        <Typography sx={{ fontSize: "0.86rem", fontWeight: 700, color: N.popText, mb: 0.4 }}>No Customers Today</Typography>
                        <Typography sx={{ fontSize: "0.74rem", color: N.popMuted }}>New orders placed today will appear here.</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ maxHeight: 420, overflowY: "auto", py: 1, "&::-webkit-scrollbar": { width: "3px" }, "&::-webkit-scrollbar-thumb": { background: "rgba(184,137,44,0.22)", borderRadius: "3px" } }}>
                        {visibleCustomers.map((n) => {
                          const displayName = n.customer_name || n.customer_email || 'Unknown Customer';
                          const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                          const avatarBg = nameColor(displayName);
                          return (
                            <Box
                              key={n.order_id}
                              sx={{
                                mx: 1.5, mb: 0.8, px: 1.3, py: 1.1,
                                borderRadius: "12px",
                                background: "rgba(184,137,44,0.04)",
                                border: "1px solid rgba(184,137,44,0.10)",
                                display: "flex", alignItems: "flex-start", gap: 1.1,
                                transition: "background 0.12s",
                                "&:hover": { background: "rgba(184,137,44,0.07)" },
                              }}
                            >
                              {/* Avatar */}
                              <Box sx={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0, background: avatarBg, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 2px 8px ${avatarBg}55` }}>
                                <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, color: "#fff", lineHeight: 1 }}>{initials}</Typography>
                              </Box>

                              {/* Info */}
                              <Box sx={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
                                <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: N.popText, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {displayName}
                                </Typography>
                                {n.customer_email && (
                                  <Typography sx={{ fontSize: "0.65rem", color: N.popMuted, mt: 0.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {n.customer_email}
                                  </Typography>
                                )}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.4, flexWrap: "wrap" }}>
                                  <Typography sx={{ fontSize: "0.63rem", fontFamily: "monospace", fontWeight: 700, color: N.accent }}>#{n.order_id}</Typography>
                                  <Box sx={{ width: 2, height: 2, borderRadius: "50%", background: "#d1d5db", flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: "0.63rem", color: N.popMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 90 }}>{n.store_name}</Typography>
                                  <Box sx={{ width: 2, height: 2, borderRadius: "50%", background: "#d1d5db", flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: "0.63rem", fontWeight: 700, color: "#b8892c" }}>${Number(n.amount || 0).toFixed(2)}</Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mt: 0.35 }}>
                                  <Box sx={{ px: 0.7, py: 0.18, borderRadius: "6px", background: "rgba(184,137,44,0.08)", border: "1px solid rgba(184,137,44,0.18)", flexShrink: 0 }}>
                                    <Typography sx={{ fontSize: "0.58rem", fontWeight: 700, color: N.accent, lineHeight: 1.4, whiteSpace: "nowrap" }}>
                                      {new Date(n.created_at || n.order_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.58rem", fontWeight: 600, color: N.popMuted, lineHeight: 1.4, whiteSpace: "nowrap" }}>
                                      {new Date(n.created_at || n.order_date).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                                    </Typography>
                                  </Box>
                                  {n.product_image && (
                                    <Box component="img" src={n.product_image} alt="" sx={{ width: 20, height: 20, borderRadius: "5px", objectFit: "cover", border: "1px solid rgba(184,137,44,0.15)", flexShrink: 0 }} />
                                  )}
                                  <Typography sx={{ fontSize: "0.61rem", color: N.popMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {n.product_name}
                                  </Typography>
                                </Box>
                                {!isCustomerRole && (
                                <Box
                                  component="a"
                                  href={`${routeOf("admin.orders")}?search=${encodeURIComponent(n.order_id)}&date=all`}
                                  onClick={(e) => { e.preventDefault(); setShowContacts(false); goAdmin("admin.orders", `?search=${encodeURIComponent(n.order_id)}&date=all`); }}
                                  sx={{ mt: 0.6, display: "inline-flex", alignItems: "center", gap: 0.4, px: 1.1, py: 0.3, borderRadius: "20px", background: N.popGrad, border: "none", color: "#fff", fontSize: "0.61rem", fontWeight: 700, cursor: "pointer", outline: "none", textDecoration: "none", boxShadow: "0 2px 8px rgba(184,137,44,0.28)", "&:hover": { opacity: 0.88 }, transition: "opacity 0.15s" }}
                                >
                                  View Order →
                                </Box>
                                )}
                              </Box>

                              <IconButton size="small" onClick={() => handleDismissCustomer(n.order_id)} sx={{ p: 0.35, borderRadius: "6px", color: N.popMuted, flexShrink: 0, "&:hover": { color: N.accent, background: "rgba(184,137,44,0.10)" } }}>
                                <XIcon size={12} weight="bold" />
                              </IconButton>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </ClickAwayListener>

            {/* Sellvia order-cleanup history */}
            <Box sx={{ display: { xs: "none", sm: "inline-flex" } }}>
              <Tooltip title="History of Order Process">
                <Button onClick={() => setShowCleanupHistory(true)} sx={navBtnStackSx}>
                  <HistoryIcon size={18} />
                  <Box component="span" sx={navBtnStackLabelSx}>History</Box>
                </Button>
              </Tooltip>
            </Box>

            {/* Notifications */}
            <ClickAwayListener onClickAway={() => setShowNotif(false)}>
              <Box ref={notifRef} sx={{ position: "relative" }}>
                <Tooltip title="Notifications">
                  <Box sx={{ position: "relative", display: { xs: "none", sm: "inline-flex" } }}>
                    <Button onClick={handleOpenNotif} sx={navBtnStackSx}>
                      <BellIcon size={18} />
                      <Box component="span" sx={navBtnStackLabelSx}>Notifications</Box>
                    </Button>
                    {(activityNotifs.length + unseenCount + unseenLowBalance + billingNotifs.length + (visiblePayoutBank.length + taskUnreadCount) + accountUnread) > 0 && (
                      <Box sx={{
                        position: "absolute", top: 3, right: 3,
                        minWidth: 16, height: 16, borderRadius: "8px",
                        background: "linear-gradient(180deg,#c99a35,#8c6a1c)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: "2px solid #fff",
                        pointerEvents: "none",
                      }}>
                        <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, color: "#fff", lineHeight: 1, px: "2px" }}>
                          {(activityNotifs.length + unseenCount + unseenLowBalance + billingNotifs.length + (visiblePayoutBank.length + taskUnreadCount) + accountUnread) > 9 ? "9+" : (activityNotifs.length + unseenCount + unseenLowBalance + billingNotifs.length + (visiblePayoutBank.length + taskUnreadCount) + accountUnread)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Tooltip>

                {showNotif && (
                  <Box sx={{ ...popCard, right: { xs: 8, sm: 0 }, left: { xs: 8, sm: "auto" }, minWidth: { xs: 0, sm: 380 }, maxWidth: { xs: "calc(100vw - 16px)", sm: 420 } }}>
                    {/* Gradient top bar */}
                    <Box sx={{ background: N.popGrad, height: 3 }} />

                    {/* Header */}
                    <Box sx={{ px: 2, py: 1.3, borderBottom: "1px solid rgba(184,137,44,0.10)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: "rgba(184,137,44,0.10)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <BellIcon size={14} color={N.accent} weight="fill" />
                        </Box>
                        <Typography sx={{ fontSize: "0.86rem", fontWeight: 800, color: N.popText }}>Notifications</Typography>
                        {(activityNotifs.length + visibleNotifs.length + visibleLowBalance.length + billingNotifs.length + (visiblePayoutBank.length + taskUnreadCount)) > 0 && (
                          <Box sx={{ px: 0.8, py: 0.15, borderRadius: "20px", background: "rgba(184,137,44,0.10)", border: "1px solid rgba(184,137,44,0.25)" }}>
                            <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, color: N.accent }}>
                              {activityNotifs.length + visibleNotifs.length + visibleLowBalance.length + billingNotifs.length + (visiblePayoutBank.length + taskUnreadCount)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                        {/* View All — admin side only (opens /admin/orders) */}
                        {!isCustomerRole && (
                        <Box
                          component="a"
                          href={routeOf("admin.orders")}
                          onClick={(e) => { e.preventDefault(); setShowNotif(false); goAdmin("admin.orders"); }}
                          sx={{ px: 1.1, py: 0.3, borderRadius: "20px", background: "rgba(184,137,44,0.09)", border: "1px solid rgba(184,137,44,0.22)", color: N.accent, fontSize: "0.62rem", fontWeight: 700, cursor: "pointer", outline: "none", textDecoration: "none", "&:hover": { background: "rgba(184,137,44,0.18)" } }}
                        >
                          View All
                        </Box>
                        )}
                        {/* Mark all read */}
                        {(activityNotifs.length > 0 || visibleNotifs.length > 0 || visibleLowBalance.length > 0 || billingNotifs.length > 0 || (visiblePayoutBank.length + taskUnreadCount) > 0) && (
                          <Box
                            component="button"
                            onClick={handleMarkAllActivityRead}
                            sx={{ px: 1, py: 0.3, borderRadius: "20px", background: "rgba(184,137,44,0.08)", border: "1px solid rgba(184,137,44,0.22)", color: "#7a5712", fontSize: "0.60rem", fontWeight: 700, cursor: "pointer", outline: "none", "&:hover": { background: "rgba(184,137,44,0.16)" } }}
                          >
                            Mark all read
                          </Box>
                        )}
                        {/* Close */}
                        <IconButton size="small" onClick={() => setShowNotif(false)} sx={{ p: 0.45, borderRadius: "7px", color: N.popMuted, "&:hover": { color: N.accent, background: "rgba(184,137,44,0.08)" } }}>
                          <XIcon size={14} weight="bold" />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Content */}
                    {activityNotifs.length === 0 && visibleNotifs.length === 0 && visibleLowBalance.length === 0 && billingNotifs.length === 0 && (visiblePayoutBank.length + taskUnreadCount) === 0 ? (
                      <Box sx={{ px: 2.5, py: 4, textAlign: "center" }}>
                        <Box sx={{ width: 48, height: 48, borderRadius: "14px", background: "rgba(184,137,44,0.08)", border: "1px solid rgba(184,137,44,0.20)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                          <BellIcon size={22} color="#b8892c" />
                        </Box>
                        <Typography sx={{ fontSize: "0.86rem", fontWeight: 700, color: N.popText, mb: 0.4 }}>All caught up!</Typography>
                        <Typography sx={{ fontSize: "0.74rem", color: N.popMuted }}>No new notifications right now.</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ maxHeight: 450, overflowY: "auto", py: 1, "&::-webkit-scrollbar": { width: "3px" }, "&::-webkit-scrollbar-thumb": { background: "rgba(184,137,44,0.22)", borderRadius: "3px" } }}>

                        {/* ── Task Manager section ── */}
                        {taskNotifs.length > 0 && (
                          <>
                            <Box sx={{ px: 2, pb: 0.5, pt: 0.2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: N.popMuted }}>
                                Tasks
                              </Typography>
                              <Box component="button" onClick={handleMarkTasksRead}
                                sx={{ border: 0, background: "transparent", cursor: "pointer", fontSize: "0.6rem", fontWeight: 700, color: N.accent, p: 0 }}>
                                Mark read
                              </Box>
                            </Box>
                            {taskNotifs.slice(0, 8).map((n) => (
                              <Box key={n._id} onClick={() => handleTaskNotifClick(n)}
                                sx={{ mx: 1.5, mb: 0.7, px: 1.3, py: 1, borderRadius: "11px", background: "rgba(184,137,44,0.05)", border: "1px solid rgba(184,137,44,0.18)", cursor: "pointer", "&:hover": { background: "rgba(184,137,44,0.10)" }, transition: "background 0.12s" }}>
                                <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: N.popText }}>{n.title}</Typography>
                                <Typography noWrap sx={{ fontSize: "0.7rem", color: N.popMuted }}>{n.message}</Typography>
                              </Box>
                            ))}
                          </>
                        )}

                        {/* ── Order Activity section ── */}
                        {activityNotifs.length > 0 && (
                          <>
                            <Box sx={{ px: 2, pb: 0.5, pt: 0.2 }}>
                              <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: N.popMuted }}>
                                Order Activity
                              </Typography>
                            </Box>
                            {activityNotifs.map((n) => {
                              const isNew = n.type === 'new_order';
                              return (
                                <Box
                                  key={n._id}
                                  onClick={() => {
                                    handleMarkActivityRead(n._id);
                                    setShowNotif(false);
                                    goAdmin("admin.orders", `?search=${encodeURIComponent(n.order_id)}&date=all`);
                                  }}
                                  sx={{
                                    mx: 1.5, mb: 0.7, px: 1.3, py: 1,
                                    borderRadius: "11px",
                                    background: isNew ? "rgba(184,137,44,0.05)" : "rgba(140,106,28,0.05)",
                                    border: `1px solid ${isNew ? "rgba(184,137,44,0.18)" : "rgba(140,106,28,0.18)"}`,
                                    display: "flex", alignItems: "flex-start", gap: 1,
                                    cursor: "pointer",
                                    "&:hover": { background: isNew ? "rgba(184,137,44,0.10)" : "rgba(140,106,28,0.10)" },
                                    transition: "background 0.12s",
                                  }}
                                >
                                  {/* Product image / icon */}
                                  <Box sx={{ width: 38, height: 38, borderRadius: "9px", flexShrink: 0, overflow: "hidden", background: isNew ? "rgba(184,137,44,0.10)" : "rgba(140,106,28,0.10)", border: `1px solid ${isNew ? "rgba(184,137,44,0.20)" : "rgba(140,106,28,0.20)"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {n.product_image ? (
                                      <Box component="img" src={n.product_image} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    ) : (
                                      <BellIcon size={15} color={isNew ? "#b8892c" : "#8c6a1c"} weight="fill" />
                                    )}
                                  </Box>

                                  <Box sx={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
                                    {/* Type badge */}
                                    <Box sx={{ display: "inline-flex", px: 0.7, py: 0.12, borderRadius: "20px", background: isNew ? "rgba(184,137,44,0.12)" : "rgba(140,106,28,0.12)", border: `1px solid ${isNew ? "rgba(184,137,44,0.30)" : "rgba(140,106,28,0.30)"}`, mb: 0.25 }}>
                                      <Typography sx={{ fontSize: "0.56rem", fontWeight: 800, color: isNew ? "#7a5712" : "#6f5212" }}>
                                        {isNew ? "NEW ORDER" : "UPDATED"}
                                      </Typography>
                                    </Box>
                                    {/* Product / order name */}
                                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: N.popText, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {n.product_name || `Order #${n.order_id}`}
                                    </Typography>
                                    {/* Details row */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25, flexWrap: "wrap" }}>
                                      <Typography sx={{ fontSize: "0.62rem", fontFamily: "monospace", fontWeight: 700, color: N.popPurple }}>#{n.order_id}</Typography>
                                      {n.customer_name && (<><Box sx={{ width: 2, height: 2, borderRadius: "50%", background: "#d1d5db", flexShrink: 0 }} /><Typography sx={{ fontSize: "0.62rem", color: N.popMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 90 }}>{n.customer_name}</Typography></>)}
                                      {n.amount > 0 && (<><Box sx={{ width: 2, height: 2, borderRadius: "50%", background: "#d1d5db", flexShrink: 0 }} /><Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#b8892c" }}>${Number(n.amount).toFixed(2)}</Typography></>)}
                                    </Box>
                                    {/* Time */}
                                    <Typography sx={{ fontSize: "0.58rem", color: N.popMuted, mt: 0.2 }}>{fmtDate(n.created_at)}{n.store_name ? ` · ${n.store_name}` : ''}</Typography>
                                  </Box>

                                  {/* Mark as read X */}
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); handleMarkActivityRead(n._id); }}
                                    sx={{ p: 0.35, borderRadius: "6px", color: N.popMuted, flexShrink: 0, mt: 0.2, "&:hover": { color: N.popAccent, background: "rgba(232,96,122,0.10)" } }}
                                  >
                                    <XIcon size={12} weight="bold" />
                                  </IconButton>
                                </Box>
                              );
                            })}
                          </>
                        )}

                        {/* ── Needs Attention section (stale orders) ── */}
                        {visibleNotifs.length > 0 && (
                          <>
                            {activityNotifs.length > 0 && (
                              <Box sx={{ mx: 1.5, my: 0.8, height: "1px", background: "rgba(0,0,0,0.07)" }} />
                            )}
                            <Box sx={{ px: 2, pb: 0.5, pt: 0.2 }}>
                              <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: N.popMuted }}>
                                Needs Attention
                              </Typography>
                            </Box>
                            {visibleNotifs.map((n) => (
                              <Box
                                key={n.order_id}
                                onClick={() => { setShowNotif(false); goAdmin("admin.orders", `?search=${encodeURIComponent(n.order_id)}&date=all`); }}
                                sx={{ mx: 1.5, mb: 0.7, px: 1.3, py: 1, borderRadius: "11px", background: "rgba(201,154,53,0.05)", border: "1px solid rgba(201,154,53,0.22)", display: "flex", alignItems: "flex-start", gap: 1, cursor: "pointer", "&:hover": { background: "rgba(201,154,53,0.10)" } }}
                              >
                                <Box sx={{ width: 38, height: 38, borderRadius: "9px", flexShrink: 0, overflow: "hidden", background: "rgba(201,154,53,0.08)", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(201,154,53,0.22)" }}>
                                  {n.product_image ? (
                                    <Box component="img" src={n.product_image} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  ) : (
                                    <WarningIcon size={16} color="#c99a35" weight="fill" />
                                  )}
                                </Box>
                                <Box sx={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
                                  <Box sx={{ display: "inline-flex", px: 0.7, py: 0.12, borderRadius: "20px", background: "rgba(201,154,53,0.10)", border: "1px solid rgba(201,154,53,0.30)", mb: 0.25 }}>
                                    <Typography sx={{ fontSize: "0.56rem", fontWeight: 700, color: "#8c6a1c" }}>
                                      ⚠ Process within {n.days_until_cancel}d or it will cancel
                                    </Typography>
                                  </Box>
                                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: N.popText, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {n.product_name}
                                  </Typography>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25, flexWrap: "wrap" }}>
                                    <Typography sx={{ fontSize: "0.62rem", color: N.popPurple, fontFamily: "monospace", fontWeight: 700 }}>#{n.order_id}</Typography>
                                    <Box sx={{ width: 2, height: 2, borderRadius: "50%", background: "#d1d5db", flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: "0.62rem", color: N.popMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.store_name}</Typography>
                                  </Box>
                                  <Typography sx={{ fontSize: "0.58rem", color: N.popMuted, mt: 0.2 }}>{fmtDate(n.order_date)}</Typography>
                                </Box>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDismissNotif(n.order_id); }} sx={{ p: 0.35, borderRadius: "6px", color: N.popMuted, flexShrink: 0, mt: 0.2, "&:hover": { color: N.popAccent, background: "rgba(232,96,122,0.10)" } }}>
                                  <XIcon size={12} weight="bold" />
                                </IconButton>
                              </Box>
                            ))}
                          </>
                        )}

                        {/* ── Ads Balance section (low Sellvia Ads balance) ── */}
                        {visibleLowBalance.length > 0 && (
                          <>
                            {(activityNotifs.length > 0 || visibleNotifs.length > 0) && (
                              <Box sx={{ mx: 1.5, my: 0.8, height: "1px", background: "rgba(0,0,0,0.07)" }} />
                            )}
                            <Box sx={{ px: 2, pb: 0.5, pt: 0.2 }}>
                              <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: N.popMuted }}>
                                Ads Balance
                              </Typography>
                            </Box>
                            {visibleLowBalance.map((n) => (
                              <Box
                                key={n.store_id}
                                onClick={() => { setShowNotif(false); goAdmin("admin.ads", `?store=${encodeURIComponent(n.store_id)}`); }}
                                sx={{ mx: 1.5, mb: 0.7, px: 1.3, py: 1, borderRadius: "11px", background: "rgba(184,137,44,0.05)", border: "1px solid rgba(184,137,44,0.22)", display: "flex", alignItems: "flex-start", gap: 1, cursor: "pointer", "&:hover": { background: "rgba(184,137,44,0.10)" } }}
                              >
                                <Box sx={{ width: 38, height: 38, borderRadius: "9px", flexShrink: 0, background: "rgba(184,137,44,0.08)", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(184,137,44,0.22)" }}>
                                  <WarningIcon size={16} color="#8c6a1c" weight="fill" />
                                </Box>
                                <Box sx={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
                                  <Box sx={{ display: "inline-flex", px: 0.7, py: 0.12, borderRadius: "20px", background: "rgba(184,137,44,0.10)", border: "1px solid rgba(184,137,44,0.30)", mb: 0.25 }}>
                                    <Typography sx={{ fontSize: "0.56rem", fontWeight: 700, color: "#7a5712" }}>
                                      ⚠ Ads balance is low — recharge needed
                                    </Typography>
                                  </Box>
                                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: N.popText, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {n.store_name}
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.62rem", color: N.popMuted, mt: 0.2 }}>
                                    Balance: ${(n.available / 100).toFixed(2)} · Needs: ${(n.threshold / 100).toFixed(2)}
                                  </Typography>
                                </Box>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDismissLowBalance(n.store_id); }} sx={{ p: 0.35, borderRadius: "6px", color: N.popMuted, flexShrink: 0, mt: 0.2, "&:hover": { color: N.popAccent, background: "rgba(232,96,122,0.10)" } }}>
                                  <XIcon size={12} weight="bold" />
                                </IconButton>
                              </Box>
                            ))}
                          </>
                        )}

                        {/* ── Billing section (new bills from the billing sync cron) ── */}
                        {billingNotifs.length > 0 && (
                          <>
                            {(activityNotifs.length > 0 || visibleNotifs.length > 0 || visibleLowBalance.length > 0) && (
                              <Box sx={{ mx: 1.5, my: 0.8, height: "1px", background: "rgba(0,0,0,0.07)" }} />
                            )}
                            <Box sx={{ px: 2, pb: 0.5, pt: 0.2 }}>
                              <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: N.popMuted }}>
                                Billing
                              </Typography>
                            </Box>
                            {billingNotifs.map((n) => (
                              <Box
                                key={n._id}
                                onClick={() => handleBillingNotifClick(n)}
                                sx={{ mx: 1.5, mb: 0.7, px: 1.3, py: 1, borderRadius: "11px", background: "rgba(184,137,44,0.05)", border: "1px solid rgba(184,137,44,0.18)", display: "flex", alignItems: "flex-start", gap: 1, cursor: "pointer", "&:hover": { background: "rgba(184,137,44,0.10)" } }}
                              >
                                <Box sx={{ width: 38, height: 38, borderRadius: "9px", flexShrink: 0, background: "rgba(184,137,44,0.10)", border: "1px solid rgba(184,137,44,0.20)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <ReceiptIcon size={16} color="#b8892c" weight="fill" />
                                </Box>
                                <Box sx={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
                                  <Box sx={{ display: "inline-flex", px: 0.7, py: 0.12, borderRadius: "20px", background: "rgba(184,137,44,0.12)", border: "1px solid rgba(184,137,44,0.30)", mb: 0.25 }}>
                                    <Typography sx={{ fontSize: "0.56rem", fontWeight: 800, color: "#7a5712" }}>NEW BILL</Typography>
                                  </Box>
                                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: N.popText, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {n.store_name || `Store ${n.store_id}`}
                                  </Typography>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25, flexWrap: "wrap" }}>
                                    {n.amount > 0 && (<Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#b8892c" }}>${(Number(n.amount) / 100).toFixed(2)}</Typography>)}
                                    {n.payment_type && (<><Box sx={{ width: 2, height: 2, borderRadius: "50%", background: "#d1d5db", flexShrink: 0 }} /><Typography sx={{ fontSize: "0.62rem", color: N.popMuted }}>{n.payment_type}</Typography></>)}
                                  </Box>
                                  <Typography sx={{ fontSize: "0.58rem", color: N.popMuted, mt: 0.2 }}>{fmtDate(n.created_at)}</Typography>
                                </Box>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMarkBillingRead(n._id); }} sx={{ p: 0.35, borderRadius: "6px", color: N.popMuted, flexShrink: 0, mt: 0.2, "&:hover": { color: N.popAccent, background: "rgba(232,96,122,0.10)" } }}>
                                  <XIcon size={12} weight="bold" />
                                </IconButton>
                              </Box>
                            ))}
                          </>
                        )}

                        {/* ── Payout section (Bank-Status still Pending 3+ days after Sent) ── */}
                        {visiblePayoutBank.length > 0 && (
                          <>
                            {(activityNotifs.length > 0 || visibleNotifs.length > 0 || visibleLowBalance.length > 0 || billingNotifs.length > 0) && (
                              <Box sx={{ mx: 1.5, my: 0.8, height: "1px", background: "rgba(0,0,0,0.07)" }} />
                            )}
                            <Box sx={{ px: 2, pb: 0.5, pt: 0.2 }}>
                              <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: N.popMuted }}>
                                Payout
                              </Typography>
                            </Box>
                            {visiblePayoutBank.map((n) => (
                              <Box
                                key={n.store_id}
                                onClick={() => { setShowNotif(false); goAdmin("admin.payout", `?store=${encodeURIComponent(n.store_id)}`); }}
                                sx={{ mx: 1.5, mb: 0.7, px: 1.3, py: 1, borderRadius: "11px", background: "rgba(140,106,28,0.05)", border: "1px solid rgba(140,106,28,0.18)", display: "flex", alignItems: "flex-start", gap: 1, cursor: "pointer", "&:hover": { background: "rgba(140,106,28,0.10)" } }}
                              >
                                <Box sx={{ width: 38, height: 38, borderRadius: "9px", flexShrink: 0, background: "rgba(140,106,28,0.10)", border: "1px solid rgba(140,106,28,0.20)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <BankIcon size={16} color="#8c6a1c" weight="fill" />
                                </Box>
                                <Box sx={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
                                  <Box sx={{ display: "inline-flex", px: 0.7, py: 0.12, borderRadius: "20px", background: "rgba(140,106,28,0.12)", border: "1px solid rgba(140,106,28,0.30)", mb: 0.25 }}>
                                    <Typography sx={{ fontSize: "0.56rem", fontWeight: 700, color: "#6f5212" }}>
                                      ⚠ Please update your payout Bank Status
                                    </Typography>
                                  </Box>
                                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: N.popText, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {n.store_name || `Store ${n.store_id}`}
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.62rem", color: N.popMuted, mt: 0.2 }}>
                                    {n.company_name} · Sent {n.days_overdue} day{n.days_overdue !== 1 ? "s" : ""} ago, still Pending
                                  </Typography>
                                </Box>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDismissPayoutBank(n.store_id); }} sx={{ p: 0.35, borderRadius: "6px", color: N.popMuted, flexShrink: 0, mt: 0.2, "&:hover": { color: N.popAccent, background: "rgba(232,96,122,0.10)" } }}>
                                  <XIcon size={12} weight="bold" />
                                </IconButton>
                              </Box>
                            ))}
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </ClickAwayListener>

            {/* the stores assigned to this customer, before the profile icon */}
            {isCustomerRole && <NavStoreChips />}

            {/* Profile avatar */}
            <ClickAwayListener onClickAway={() => setShowProfile(false)}>
              <Box ref={profileRef} sx={{ position: "relative" }}>
                <Box
                  onClick={() => { setShowProfile(p => !p); setShowNotif(false); setShowContacts(false); }}
                  sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 1, pl: 0.5 }}
                >
                  <Avatar
                    src={profileImage}
                    sx={{
                      width: 36, height: 36,
                      border: `2px solid ${showProfile ? N.accent : "rgba(184,137,44,0.25)"}`,
                      boxShadow: showProfile ? `0 0 0 3px rgba(184,137,44,0.2)` : "none",
                      transition: "all 0.2s ease",
                      fontSize: "0.85rem", fontWeight: 700,
                      background: "linear-gradient(135deg, #fbf3df, #f0dcae)",
                      color: "#7a5712",
                    }}
                  >
                    {!profileImage && (user?.name?.[0]?.toUpperCase() || "U")}
                  </Avatar>
                </Box>

                {showProfile && (
                  <Fade in>
                    <Box sx={{ ...popCard, right: { xs: 8, sm: 0 }, left: { xs: 8, sm: "auto" }, minWidth: { xs: 0, sm: 300 } }}>
                      {/* Brand mark */}
                      {/* Brand mark — same logo + "Market"(dark) "Sync"(gold) as the sidebar */}
                      <Box sx={{ px: 2.5, pt: 1.6, pb: 0.6, display: "flex", alignItems: "center", gap: "8px" }}>
                        <Box component="img" src="/assests/website-logo.png" alt="MarketSync" sx={{ width: 24, height: "auto", objectFit: "contain", flexShrink: 0 }} />
                        <Typography noWrap sx={{ fontSize: "1rem", fontWeight: 800, letterSpacing: "0.01em", lineHeight: 1.2 }}>
                          <Box component="span" sx={{ color: "#14161c" }}>Market</Box>
                          <Box component="span" sx={{ background: "linear-gradient(135deg, #fbbf24, #e4870e)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>Sync</Box>
                        </Typography>
                      </Box>

                      {/* User info header */}
                      <Box sx={{ px: 2.5, py: 2.5, background: "linear-gradient(135deg, rgba(184,137,44,0.15), rgba(109,40,217,0.08))", borderBottom: `1px solid ${N.popBorder}` }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.8 }}>
                          <Avatar
                            src={profileImage}
                            sx={{
                              width: 44, height: 44,
                              background: "linear-gradient(135deg, #fbf3df, #f0dcae)",
                              border: "2px solid rgba(216,178,90,0.6)",
                              fontSize: "1rem", fontWeight: 800, color: "#7a5712",
                            }}
                          >
                            {!profileImage && (user?.name?.[0]?.toUpperCase() || "U")}
                          </Avatar>
                          <Box sx={{ overflow: "hidden" }}>
                            <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: N.popText, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {user?.name || "User"}
                            </Typography>
                            <Typography sx={{ fontSize: "0.73rem", color: N.popMuted, mt: 0.3 }}>
                              {user?.email || ""}
                            </Typography>
                            <Box sx={{ display: "inline-flex", mt: 0.6, px: 1, py: 0.2, borderRadius: "5px", background: "rgba(184,137,44,0.18)", border: "1px solid rgba(184,137,44,0.3)" }}>
                              <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: N.accent }}>
                                {role || "User"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      {/* Phones only — Ask AI / Customers / History / Notifications
                          live here instead of the top bar. Opened on the next tick
                          so this same click doesn't count as a "click away" for
                          the panel being opened. */}
                      <Box sx={{ display: { xs: "block", sm: "none" }, pt: 1 }}>
                        {[
                          { key: "ai", label: "Ask AI", Icon: Sparkle, open: () => { setOpenNav(false); setActivePanel("chat"); } },
                          { key: "customers", label: "Customers", Icon: UsersIcon, count: unseenCustomers, open: handleOpenContacts },
                          { key: "history", label: "History", Icon: HistoryIcon, open: () => setShowCleanupHistory(true) },
                          { key: "notifications", label: "Notifications", Icon: BellIcon, count: activityNotifs.length + unseenCount + unseenLowBalance + billingNotifs.length + (visiblePayoutBank.length + taskUnreadCount), open: handleOpenNotif },
                        ].map(({ key, label, Icon, count, open }) => (
                          <Box
                            key={key}
                            onClick={() => { setShowProfile(false); setTimeout(open, 0); }}
                            sx={{
                              px: 2.5, py: 1, display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer",
                              transition: "background 0.14s", "&:hover": { background: "rgba(184,137,44,0.08)" },
                            }}
                          >
                            <Box sx={{ width: 26, height: 26, borderRadius: "7px", background: "rgba(184,137,44,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Icon size={13} color={N.accent} />
                            </Box>
                            <Typography sx={{ fontSize: "0.82rem", color: N.popText, flex: 1 }}>{label}</Typography>
                            {count > 0 && (
                              <Box sx={{ minWidth: 20, height: 18, px: 0.6, borderRadius: "9px", background: N.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Typography sx={{ fontSize: "0.62rem", fontWeight: 800, color: "#fff", lineHeight: 1 }}>{count > 9 ? "9+" : count}</Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                        <Box sx={{ mx: 2, my: 1, height: "1px", background: N.popBorder }} />
                      </Box>

                      {/* Settings / Account modules — only if user has access */}
                      {accessibleModules.length > 0 && (
                        <>
                          <Box sx={{ pt: 1 }} />
                          {accessibleModules.map((m) => {
                            const ModIcon = (m.icon && navIcons[m.icon]) || ChartBar;
                            const isCurrent = location.pathname === m.route_path.trim() || location.pathname.startsWith(`${m.route_path.trim()}/`);
                            return (
                              <Box
                                key={m.moduleId || m._id}
                                component="a"
                                href={m.route_path}
                                onClick={(e) => {
                                  e.preventDefault();
                                  // Settings always opens on its first available tab (see SettingsPage's settingsHome effect).
                                  const isSettings = m.route_path.trim() === routeOf("admin.settings");
                                  navigate(m.route_path, isSettings ? { state: { settingsHome: Date.now() } } : undefined);
                                  setShowProfile(false);
                                }}
                                sx={{
                                  mx: 1.5, my: 0.3, px: 1.5, py: 1, borderRadius: "10px",
                                  display: "flex", alignItems: "center", gap: 1.5,
                                  cursor: "pointer",
                                  textDecoration: "none",
                                  // colour sweeps in from the LEFT to the right on hover; the page you are on stays fully filled
                                  backgroundImage: "linear-gradient(90deg, #8c6a1c 0%, #b8892c 60%, #d8b25a 100%)",
                                  backgroundRepeat: "no-repeat",
                                  backgroundSize: isCurrent ? "100% 100%" : "0% 100%",
                                  transition: "background-size 0.35s ease, box-shadow 0.2s ease",
                                  boxShadow: isCurrent ? "0 4px 12px rgba(111,82,18,0.28)" : "none",
                                  "&:hover": { backgroundSize: "100% 100%", boxShadow: "0 4px 12px rgba(111,82,18,0.28)" },
                                  "& .acct-ico": { transition: "background 0.2s ease", background: isCurrent ? "rgba(255,255,255,0.22)" : "rgba(184,137,44,0.12)" },
                                  "& .acct-ico svg": { transition: "color 0.2s ease", color: isCurrent ? "#fff" : N.accent },
                                  "& .acct-txt": { transition: "color 0.2s ease", color: isCurrent ? "#fff" : N.popText, fontWeight: isCurrent ? 700 : 500 },
                                  "&:hover .acct-ico": { background: "rgba(255,255,255,0.22)" },
                                  "&:hover .acct-ico svg": { color: "#fff" },
                                  "&:hover .acct-txt": { color: "#fff", fontWeight: 700 },
                                }}
                              >
                                <Box className="acct-ico" sx={{ width: 26, height: 26, borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <ModIcon size={13} color="currentColor" />
                                </Box>
                                <Typography className="acct-txt" sx={{ fontSize: "0.82rem" }}>{m.name}</Typography>
                              </Box>
                            );
                          })}
                          <Box sx={{ mx: 2, my: 1, height: "1px", background: N.popBorder }} />
                        </>
                      )}

                      {/* Logout */}
                      <Box sx={{ px: 1.5, py: 1 }}>
                        <Box
                          onClick={handleLogout}
                          sx={{
                            display: "flex", alignItems: "center", gap: 1.5,
                            px: 1.5, py: 1, borderRadius: "10px", cursor: "pointer",
                            transition: "all 0.16s",
                            "&:hover": { background: "rgba(251,113,133,0.1)" },
                          }}
                        >
                          <SignOut size={16} color="#dc2626" />
                          <Typography sx={{ fontSize: "0.83rem", fontWeight: 700, color: "#dc2626" }}>
                            Sign Out
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Fade>
                )}
              </Box>
            </ClickAwayListener>
          </Stack>
        </Stack>

      </Box>

      {/* Financials alert (low Ads balance / overdue payout / new bill, one at a time) — not part of the nav:
          it stacks in the bottom-right corner with the other toasts (the Ask-AI button lifts above it). */}
        {currentAlert && (
          <Box sx={{ display: { xs: "none", md: "block" }, position: "fixed", bottom: 24 + 84 * visibleToastCount, right: 24, zIndex: 1250, maxWidth: "calc(100vw - 48px)", borderRadius: "12px", background: "#fffaf0", boxShadow: "0 10px 30px rgba(122,87,18,0.22)" }}>
            <Box
              key={currentAlert.key}
              sx={{
                display: "flex",
                alignItems: "center", gap: 0.9,
                px: 1.3, py: 0.7, borderRadius: "12px",
                background: "rgba(184,137,44,0.10)",
                border: "1px solid rgba(184,137,44,0.30)",
                minWidth: 0, maxWidth: 560,
                animation: "toastSlideIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
              }}
            >
              {/* Cancel icon on the left, ahead of the message — skips to
                  the next item in the queue rather than dismissing for
                  good, since this ticker is meant to keep recurring. */}
              <IconButton
                size="small"
                onClick={() => setShowAlertTicker(false)}
                sx={{ p: 0.35, borderRadius: "6px", color: "#8c6a1c", flexShrink: 0, "&:hover": { background: "rgba(184,137,44,0.15)" } }}
              >
                <XIcon size={13} weight="bold" />
              </IconButton>
              <WarningIcon size={16} color="#8c6a1c" weight="fill" />
              <Typography
                component="a"
                href={currentAlert.path}
                onClick={(e) => { e.preventDefault(); handleAlertClick(); }}
                sx={{
                  fontSize: "0.76rem", fontWeight: 700, color: "#8c6a1c",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  cursor: "pointer", textDecoration: "none", "&:hover": { textDecoration: "underline" },
                }}
              >
                {currentAlert.message}
              </Typography>
            </Box>
                    </Box>
        )}

      {/* ── Animated gradient line under the nav ── */}
      <style>{`
        @keyframes navLineSlide {
          0%   { background-position: -100% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
      <Box sx={{
        height: "2px",
        width: "100%",
        background: "linear-gradient(90deg, transparent 0%, #8c6a1c 20%, #d8b25a 40%, #b8892c 60%, #c99a35 80%, transparent 100%)",
        backgroundSize: "200% 100%",
        animation: "navLineSlide 3s linear infinite",
        position: "relative",
        zIndex: 1199,
        mt: "-1px",
      }} />

      <MobileNav onClose={() => setOpenNav(false)} open={openNav} />

      {/* ── Mobile-style toast: stale order re-appeared ── */}
      <style>{`
        @keyframes toastSlideIn {
          from { opacity:0; transform: translateX(100%) scale(0.92); }
          to   { opacity:1; transform: translateX(0)   scale(1); }
        }
        @keyframes toastSlideOut {
          from { opacity:1; transform: translateX(0)   scale(1); }
          to   { opacity:0; transform: translateX(100%) scale(0.92); }
        }
      `}</style>

      {showToast && (
        <Box
          onClick={() => { setShowToast(false); setToastCount(0); handleOpenNotif(); }}
          sx={{
            position: "fixed", bottom: 24, right: 24, zIndex: 2000,
            display: "flex", alignItems: "center", gap: 1.2,
            px: 1.6, py: 1.2, borderRadius: "16px",
            background: "#fff",
            border: "1px solid rgba(232,96,122,0.25)",
            boxShadow: "0 8px 32px rgba(232,96,122,0.20), 0 2px 8px rgba(0,0,0,0.08)",
            cursor: "pointer", maxWidth: 300,
            animation: "toastSlideIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
            "&:hover": { boxShadow: "0 12px 40px rgba(232,96,122,0.28)" },
            transition: "box-shadow 0.2s",
          }}
        >
          {/* gradient top line */}
          <Box sx={{ position:"absolute", top:0, left:0, right:0, height:"3px", borderRadius:"16px 16px 0 0", background: N.popGrad }} />
          <Box sx={{ width: 36, height: 36, borderRadius: "11px", background: "rgba(232,96,122,0.10)", border: "1px solid rgba(232,96,122,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <BellIcon size={17} color={N.popAccent} weight="fill" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: N.popText, lineHeight: 1.2 }}>
              {toastCount} order{toastCount > 1 ? "s" : ""} need attention
            </Typography>
            <Typography sx={{ fontSize: "0.66rem", color: N.popMuted, mt: 0.15 }}>
              Stale order re-appeared · tap to view
            </Typography>
          </Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShowToast(false); setToastCount(0); }} sx={{ p: 0.4, borderRadius: "6px", color: N.popMuted, flexShrink: 0, "&:hover": { color: N.popAccent, background: "rgba(232,96,122,0.10)" } }}>
            <XIcon size={12} weight="bold" />
          </IconButton>
        </Box>
      )}

      {/* ── Session expiry warning — centered modal with a circular
          countdown ring, redesigned to match a reference "session about
          to expire" dialog: ring + time inside it, title, divider,
          message, and exactly one button (Renew) — no dismiss/close,
          since the whole point is to force a choice before the 30s
          window (see the tick() effect above) runs out. ── */}
      {showSessionWarning && sessionSecsLeft > 0 && (() => {
        const SESSION_WARNING_TOTAL_SECS = 30;
        const pct = Math.max(0, Math.min(100, (sessionSecsLeft / SESSION_WARNING_TOTAL_SECS) * 100));
        const ringColor = sessionSecsLeft <= 10 ? "#ef4444" : "#f59e0b";
        return (
          <Box sx={{
            position: "fixed", inset: 0, zIndex: 2200,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(15,15,26,0.55)", backdropFilter: "blur(2px)",
            animation: "toastSlideIn 0.25s ease both",
          }}>
            <Box sx={{
              width: 340, borderRadius: "20px", background: "#fff",
              boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
              p: { xs: 3, sm: 3.5 }, textAlign: "center",
            }}>
              {/* circular countdown ring — conic-gradient sweep with the
                  seconds shown in the punched-out white center */}
              <Box sx={{
                width: 96, height: 96, borderRadius: "50%", mx: "auto", mb: 2.5,
                background: `conic-gradient(${ringColor} ${pct}%, rgba(0,0,0,0.08) ${pct}%)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "background 1s linear",
              }}>
                <Box sx={{
                  width: 78, height: 78, borderRadius: "50%", bgcolor: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Typography sx={{ fontWeight: 900, fontSize: "1.7rem", lineHeight: 1, color: ringColor, transition: "color 0.4s" }}>
                    {sessionSecsLeft}
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", color: N.text, lineHeight: 1.25, mb: 2 }}>
                Your session is about to expire
              </Typography>

              <Box sx={{ height: "1px", background: "rgba(0,0,0,0.10)", mb: 2 }} />

              <Typography sx={{ fontSize: "0.88rem", color: N.muted, lineHeight: 1.5, mb: 2.5 }}>
                You'll be logged out in <strong style={{ color: N.text }}>{sessionSecsLeft}</strong> second{sessionSecsLeft === 1 ? "" : "s"}.
                <br />Do you want to stay signed in?
              </Typography>

              <Box
                component="button"
                onClick={handleRenewClick}
                disabled={renewLoading}
                sx={{
                  width: "100%", py: 1.2, borderRadius: "12px",
                  background: renewLoading ? "rgba(184,137,44,0.55)" : "linear-gradient(135deg,#b8892c,#fbbf24)",
                  border: "none", color: "#fff", fontSize: "0.86rem", fontWeight: 800, letterSpacing: "0.02em",
                  cursor: renewLoading ? "default" : "pointer", outline: "none",
                  boxShadow: renewLoading ? "none" : "0 6px 18px rgba(184,137,44,0.35)",
                  "&:hover": { opacity: renewLoading ? 1 : 0.92 },
                  transition: "opacity 0.15s",
                }}
              >
                {renewLoading ? "Renewing…" : "YES, KEEP ME SIGNED IN"}
              </Box>
            </Box>
          </Box>
        );
      })()}

      {/* ── Toast: new order activity ── */}
      {activityToast && (
        <Box
          onClick={() => { setActivityToast(null); setShowNotif(true); }}
          sx={{
            position: "fixed", bottom: 24, right: 24, zIndex: 2100,
            display: "flex", alignItems: "center", gap: 1.2,
            px: 1.6, py: 1.2, borderRadius: "16px",
            background: "#fff",
            border: "1px solid rgba(184,137,44,0.28)",
            boxShadow: "0 8px 32px rgba(184,137,44,0.20), 0 2px 8px rgba(0,0,0,0.08)",
            cursor: "pointer", maxWidth: 300,
            animation: "toastSlideIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
            "&:hover": { boxShadow: "0 12px 40px rgba(184,137,44,0.28)" },
            transition: "box-shadow 0.2s",
          }}
        >
          <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", borderRadius: "16px 16px 0 0", background: "linear-gradient(90deg, #d8b25a, #8c6a1c)" }} />
          <Box sx={{ width: 36, height: 36, borderRadius: "11px", background: "rgba(184,137,44,0.12)", border: "1px solid rgba(184,137,44,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <BellIcon size={17} color="#b8892c" weight="fill" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: N.popText, lineHeight: 1.2 }}>
              {activityToast.count} new order{activityToast.count > 1 ? "s" : ""} saved
            </Typography>
            <Typography sx={{ fontSize: "0.66rem", color: N.popMuted, mt: 0.15 }}>
              {activityToast.latest?.customer_name ? `${activityToast.latest.customer_name} · ` : ''}tap to view
            </Typography>
          </Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setActivityToast(null); }} sx={{ p: 0.4, borderRadius: "6px", color: N.popMuted, flexShrink: 0, "&:hover": { color: "#b8892c", background: "rgba(184,137,44,0.10)" } }}>
            <XIcon size={12} weight="bold" />
          </IconButton>
        </Box>
      )}

      {/* ── Toast: new bill ── */}
      {billingToast && (
        <Box
          onClick={() => { setBillingToast(null); setShowNotif(true); }}
          sx={{
            position: "fixed", bottom: activityToast ? 108 : 24, right: 24, zIndex: 2100,
            display: "flex", alignItems: "center", gap: 1.2,
            px: 1.6, py: 1.2, borderRadius: "16px",
            background: "#fff",
            border: "1px solid rgba(184,137,44,0.28)",
            boxShadow: "0 8px 32px rgba(184,137,44,0.20), 0 2px 8px rgba(0,0,0,0.08)",
            cursor: "pointer", maxWidth: 300,
            animation: "toastSlideIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
            "&:hover": { boxShadow: "0 12px 40px rgba(184,137,44,0.28)" },
            transition: "box-shadow 0.2s, bottom 0.3s",
          }}
        >
          <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", borderRadius: "16px 16px 0 0", background: "linear-gradient(90deg, #d8b25a, #8c6a1c)" }} />
          <Box sx={{ width: 36, height: 36, borderRadius: "11px", background: "rgba(184,137,44,0.12)", border: "1px solid rgba(184,137,44,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ReceiptIcon size={17} color="#b8892c" weight="fill" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: N.popText, lineHeight: 1.2 }}>
              {billingToast.count} new bill{billingToast.count > 1 ? "s" : ""}
            </Typography>
            <Typography sx={{ fontSize: "0.66rem", color: N.popMuted, mt: 0.15 }}>
              {billingToast.latest?.store_name ? `${billingToast.latest.store_name} · ` : ''}tap to view
            </Typography>
          </Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setBillingToast(null); }} sx={{ p: 0.4, borderRadius: "6px", color: N.popMuted, flexShrink: 0, "&:hover": { color: "#b8892c", background: "rgba(184,137,44,0.10)" } }}>
            <XIcon size={12} weight="bold" />
          </IconButton>
        </Box>
      )}

      {/* ── Mobile-style toast: new customer order ── */}
      {showCustomerToast && customerToastData && (
        <Box
          onClick={() => { setShowCustomerToast(false); handleOpenContacts(); }}
          sx={{
            position: "fixed", bottom: (activityToast && billingToast) ? 192 : (activityToast || billingToast) ? 108 : (showToast ? 108 : 24), right: 24, zIndex: 2000,
            display: "flex", alignItems: "center", gap: 1.2,
            px: 1.6, py: 1.2, borderRadius: "16px",
            background: "#fff",
            border: "1px solid rgba(184,137,44,0.22)",
            boxShadow: "0 8px 32px rgba(184,137,44,0.18), 0 2px 8px rgba(0,0,0,0.08)",
            cursor: "pointer", maxWidth: 300,
            animation: "toastSlideIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
            "&:hover": { boxShadow: "0 12px 40px rgba(184,137,44,0.26)" },
            transition: "box-shadow 0.2s, bottom 0.3s",
          }}
        >
          {/* gradient top line */}
          <Box sx={{ position:"absolute", top:0, left:0, right:0, height:"3px", borderRadius:"16px 16px 0 0", background: N.popGrad }} />
          {/* Avatar */}
          <Box sx={{
            width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
            background: nameColor(customerToastData.customer_name || ''),
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 2px 8px ${nameColor(customerToastData.customer_name || '')}55`,
          }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, color: "#fff", lineHeight: 1 }}>
              {(customerToastData.customer_name || 'U').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: N.popText, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              New order — {customerToastData.customer_name || 'Customer'}
            </Typography>
            <Typography sx={{ fontSize: "0.66rem", color: N.popMuted, mt: 0.15 }}>
              ${Number(customerToastData.amount || 0).toFixed(2)} · {customerToastData.store_name} · tap to view
            </Typography>
          </Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShowCustomerToast(false); }} sx={{ p: 0.4, borderRadius: "6px", color: N.popMuted, flexShrink: 0, "&:hover": { color: N.popPurple, background: "rgba(184,137,44,0.10)" } }}>
            <XIcon size={12} weight="bold" />
          </IconButton>
        </Box>
      )}

      {/* ── Mobile-style toast: low Sellvia Ads balance ── */}
      {showLowBalanceToast && lowBalanceToastData && (
        <Box
          onClick={() => { setShowLowBalanceToast(false); goAdmin("admin.ads", `?store=${encodeURIComponent(lowBalanceToastData.store_id)}`); }}
          sx={{
            position: "fixed",
            bottom: (activityToast || billingToast || showToast || showCustomerToast) ? 108 : 24,
            right: 24, zIndex: 2000,
            display: "flex", alignItems: "center", gap: 1.2,
            px: 1.6, py: 1.2, borderRadius: "16px",
            background: "#fff",
            border: "1px solid rgba(184,137,44,0.28)",
            boxShadow: "0 8px 32px rgba(184,137,44,0.20), 0 2px 8px rgba(0,0,0,0.08)",
            cursor: "pointer", maxWidth: 300,
            animation: "toastSlideIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
            "&:hover": { boxShadow: "0 12px 40px rgba(184,137,44,0.28)" },
            transition: "box-shadow 0.2s, bottom 0.3s",
          }}
        >
          <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", borderRadius: "16px 16px 0 0", background: "linear-gradient(90deg, #c99a35, #7a5712)" }} />
          <Box sx={{ width: 36, height: 36, borderRadius: "11px", background: "rgba(184,137,44,0.12)", border: "1px solid rgba(184,137,44,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <WarningIcon size={17} color="#8c6a1c" weight="fill" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: N.popText, lineHeight: 1.2 }}>
              Ads balance low — {lowBalanceToastData.store_name}
            </Typography>
            <Typography sx={{ fontSize: "0.66rem", color: N.popMuted, mt: 0.15 }}>
              Please recharge · tap to view
            </Typography>
          </Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShowLowBalanceToast(false); }} sx={{ p: 0.4, borderRadius: "6px", color: N.popMuted, flexShrink: 0, "&:hover": { color: "#8c6a1c", background: "rgba(184,137,44,0.10)" } }}>
            <XIcon size={12} weight="bold" />
          </IconButton>
        </Box>
      )}

      <SellviaCleanupHistoryPanel open={showCleanupHistory} onClose={() => setShowCleanupHistory(false)} />

      {/* Two-Step Verification-gated session renewal — only ever opens for
          accounts with 2FA on; everyone else still renews silently above. */}
      <Dialog open={sessionVerifyOpen} onClose={closeSessionVerify} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Verify to Continue</DialogTitle>
        <DialogContent>
          {sessionVerifyMethod === null ? (
            <Stack spacing={1.5} sx={{ pt: 0.5 }}>
              <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                Your session is about to expire. Choose how you'd like to verify to stay signed in.
              </Typography>
              <Button
                variant="outlined" onClick={chooseSessionEmail} disabled={sessionSendingEmail}
                startIcon={sessionSendingEmail ? <CircularProgress size={14} /> : null}
                sx={{ justifyContent: "flex-start", textTransform: "none", py: 1.2 }}
              >
                📧 Verify by Email
              </Button>
              <Button
                variant="outlined" onClick={() => setSessionVerifyMethod('authenticator')}
                sx={{ justifyContent: "flex-start", textTransform: "none", py: 1.2 }}
              >
                🔑 Verify with Authenticator App
              </Button>
            </Stack>
          ) : (
            <Stack spacing={1.5} sx={{ pt: 0.5 }}>
              <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                {sessionVerifyMethod === "email"
                  ? "Enter the 6-digit code we just emailed you."
                  : "Enter the current 6-digit code from your authenticator app."}
              </Typography>
              <TextField
                autoFocus
                label="Verification code"
                value={sessionOtp}
                onChange={(e) => setSessionOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ inputMode: "numeric", maxLength: 6 }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSessionVerify}>Cancel</Button>
          {sessionVerifyMethod !== null && (
            <Button
              variant="contained" onClick={confirmSessionOtp} disabled={sessionVerifying || sessionOtp.length < 6}
              startIcon={sessionVerifying ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            >
              Verify
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}